import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import { Alert, Button, Card, EmptyState, ErrorState, Field, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { formatMoney, parseMoney } from '../finance/money';
import { useMe } from '../session/SessionProvider';

type Movement = { id: string; kind: 'recarga' | 'compra' | 'devolucion' | 'ajuste'; amountCents: number; method: string | null; reference: string | null; balanceAfterCents: number; at: string };
const KIND: Record<Movement['kind'], string> = { recarga: 'Recarga', compra: 'Compra', devolucion: 'Devolución', ajuste: 'Ajuste' };
const METHOD: Record<string, string> = { efectivo: 'efectivo', transferencia: 'transferencia', en_linea: 'en línea' };
const money = (c: number) => formatMoney(c, 'MXN');
const when = (iso: string) => new Date(iso).toLocaleString('es', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

function Movements({ items }: { items: Movement[] }) {
  if (items.length === 0) return <p className="muted small">Todavía no hay movimientos.</p>;
  return (
    <ul className="list">
      {items.map((m) => (
        <li key={m.id} className="list__item">
          <span>
            <strong>{KIND[m.kind]}</strong>
            <span className="small muted"> · {when(m.at)}{m.method ? ` · ${METHOD[m.method] ?? m.method}` : ''}{m.reference ? ` · ${m.reference}` : ''}</span>
          </span>
          <span className="money">
            {m.amountCents > 0 ? '+' : '−'}{money(Math.abs(m.amountCents))} <span className="small muted">→ {money(m.balanceAfterCents)}</span>
          </span>
        </li>
      ))}
    </ul>
  );
}

/** Monedero: mi saldo para pagar dentro de la iglesia; la caja recarga. */
export function WalletPage() {
  const me = useMe();
  const churchId = me.activeChurchId;
  const base = `/commerce/churches/${churchId}`;
  const mine = useAsync((signal) => (churchId ? api.get<{ balanceCents: number; movements: Movement[]; canRechargeOnline: boolean }>(`${base}/wallet/me`, signal) : Promise.resolve(null)), [base]);
  const [params, setParams] = useSearchParams();
  const canRecharge = me.can('commerce.sell');
  const tab = canRecharge && params.get('ver') === 'caja' ? 'caja' : 'mio';
  const [msg, setMsg] = useState<{ tone: 'error' | 'success'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  if (!churchId) return <EmptyState title="Elige primero una iglesia en el selector de arriba." />;
  const pago = params.get('pago');

  async function rechargeOnline(amountText: string) {
    const cents = parseMoney(amountText);
    if (cents === null) return setMsg({ tone: 'error', text: 'Escribe cuánto quieres recargar.' });
    setBusy(true);
    setMsg(null);
    try {
      const { url } = await api.post<{ url: string }>(`${base}/wallet/me/checkout`, { amountCents: cents });
      window.location.assign(url);
    } catch (e) {
      setMsg({ tone: 'error', text: errorMessage(e) });
      setBusy(false);
    }
  }

  return (
    <div className="page page--narrow">
      <header className="page__header">
        <h1>Monedero</h1>
        <p className="muted">Tu saldo para pagar dentro de la iglesia (cafetería y más).</p>
      </header>
      {msg ? <Alert tone={msg.tone}>{msg.text}</Alert> : null}
      {pago === 'aprobado' ? <Alert tone="success">¡Listo! Tu recarga fue aprobada; en unos segundos se verá tu saldo.</Alert> : null}
      {pago === 'pendiente' ? <Alert tone="info">Tu recarga quedó pendiente (por ejemplo, en OXXO). Se abonará en cuanto se confirme.</Alert> : null}
      {canRecharge ? (
        <div className="tabs" role="tablist" aria-label="Monedero">
          {(['mio', 'caja'] as const).map((k) => (
            <button key={k} type="button" role="tab" aria-selected={tab === k} className={`tabs__tab${tab === k ? ' tabs__tab--active' : ''}`} onClick={() => setParams(k === 'mio' ? {} : { ver: k }, { replace: true })}>
              {k === 'mio' ? 'Mi monedero' : 'Recargar (caja)'}
            </button>
          ))}
        </div>
      ) : null}
      {tab === 'mio' ? (
        <>
          {mine.status === 'loading' ? <Loading /> : null}
          {mine.status === 'error' ? <ErrorState error={mine.error} onRetry={mine.reload} /> : null}
          {mine.status === 'ready' && mine.data ? (
            <>
              <Card>
                <p className="wallet__balance">
                  <span className="muted small">Saldo</span>
                  <strong>{money(mine.data.balanceCents)}</strong>
                </p>
                {mine.data.canRechargeOnline ? (
                  <form className="family-add" onSubmit={(e) => { e.preventDefault(); void rechargeOnline(String(new FormData(e.currentTarget).get('amount') ?? '')); }}>
                    <Field label="Recargar en línea" name="amount" inputMode="decimal" placeholder="200" />
                    <Button type="submit" busy={busy}>Recargar con Mercado Pago</Button>
                  </form>
                ) : (
                  <p className="small muted">Para recargar, paga en la caja de la iglesia (efectivo o transferencia).</p>
                )}
              </Card>
              <Card title="Movimientos">
                <Movements items={mine.data.movements} />
              </Card>
            </>
          ) : null}
        </>
      ) : (
        <CashierTab base={base} onMessage={setMsg} />
      )}
    </div>
  );
}

/** Recargar el monedero de alguien en caja (con confirmación). También se usa en Comercio → «Recargar monedero». */
export function CashierTab({ base, onMessage }: { base: string; onMessage: (m: { tone: 'error' | 'success'; text: string } | null) => void }) {
  const list = useAsync((signal) => api.get<{ items: { personId: string; name: string; balanceCents: number }[] }>(`${base}/wallets`, signal).then((r) => r.items), [base]);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const history = useAsync((signal) => (selected ? api.get<{ balanceCents: number; movements: Movement[] }>(`${base}/wallets/${selected}`, signal) : Promise.resolve(null)), [base, selected]);
  if (list.status === 'loading') return <Loading />;
  if (list.status === 'error') return <ErrorState error={list.error} onRetry={list.reload} />;
  const q = query.trim().toLowerCase();
  const people = list.data.filter((p) => !q || p.name.toLowerCase().includes(q));
  const person = list.data.find((p) => p.personId === selected);

  return (
    <>
      <div className="field">
        <label htmlFor="wallet-search">Buscar persona</label>
        <input id="wallet-search" type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Nombre" />
      </div>
      {person ? (
        <Card title={`${person.name} · ${money(history.status === 'ready' && history.data ? history.data.balanceCents : person.balanceCents)}`} action={<Button variant="ghost" onClick={() => setSelected(null)}>Cerrar</Button>}>
          <form
            className="form"
            onSubmit={(e) => {
              e.preventDefault();
              const form = e.currentTarget;
              const f = new FormData(form);
              const cents = parseMoney(String(f.get('amount') ?? ''));
              if (cents === null) return onMessage({ tone: 'error', text: 'Escribe el monto de la recarga.' });
              const method = String(f.get('method'));
              if (!window.confirm(`¿Recargar ${money(cents)} a ${person.name} (${method === 'transferencia' ? 'transferencia' : 'efectivo'})?`)) return;
              setBusy(true);
              onMessage(null);
              api
                .post<{ balanceCents: number }>(`${base}/wallets/${person.personId}/recharges`, { amountCents: cents, method, reference: String(f.get('reference') ?? '').trim() || null })
                .then((r) => {
                  onMessage({ tone: 'success', text: `Recarga lista. Nuevo saldo de ${person.name}: ${money(r.balanceCents)}.` });
                  form.reset();
                  list.reload();
                  history.reload();
                })
                .catch((err) => onMessage({ tone: 'error', text: errorMessage(err) }))
                .finally(() => setBusy(false));
            }}
          >
            <div className="form__row form__row--3">
              <Field label="Monto" name="amount" inputMode="decimal" required />
              <div className="field">
                <label htmlFor="wallet-method">Cómo pagó</label>
                <select id="wallet-method" name="method" defaultValue="efectivo">
                  <option value="efectivo">Efectivo</option>
                  <option value="transferencia">Transferencia</option>
                </select>
              </div>
              <Field label="Referencia (opcional)" name="reference" maxLength={200} />
            </div>
            <Button type="submit" busy={busy}>Recargar</Button>
          </form>
          {history.status === 'ready' && history.data ? <Movements items={history.data.movements} /> : null}
        </Card>
      ) : null}
      <ul className="list card">
        {people.slice(0, 100).map((p) => (
          <li key={p.personId} className="list__item">
            <button type="button" className="link-button" onClick={() => setSelected(p.personId)}>{p.name}</button>
            <span className="money">{money(p.balanceCents)}</span>
          </li>
        ))}
      </ul>
    </>
  );
}
